import { Euler, Matrix4, Object3D, Quaternion } from 'three';
import {
  Point2D,
  Point3D,
} from '../../team-apis/otterdam/answers/shape-typings';
import { Axis } from '../renderers/side-view/side-view.component';
import {
  CUBE_SIZE,
  CuboidLocationState3D,
} from '../state/cuboid-location-state-3d';
import {
  eulerToPoint,
  pointToVector,
  TentPoleVertex,
  vectorToPoint,
} from './point-cloud-utils';
import { addPoints, roundVector } from './point-utils';

export function getFaceDimensions(scale: Point3D, axis: Axis): Point2D {
  const [xIndex, yIndex] = [(axis + 1) % 3, (axis + 2) % 3].sort(
    (a, b) => a - b,
  );
  return [scale[xIndex], scale[yIndex]];
}

export function buildCuboidPointsFromMesh(object: Object3D) {
  return buildCuboidPointsFromShapeLocation({
    position: vectorToPoint(object.position),
    rotation: eulerToPoint(object.rotation),
    scale: vectorToPoint(object.scale),
  });
}

export function buildCuboidPoints3D({
  position,
  scale,
}: {
  position: Point3D;
  scale: Point3D;
}) {
  const offsetX = scale[0] / 2;
  const offsetY = scale[1] / 2;
  const offsetZ = scale[2] / 2;

  const points: Point3D[] = [];

  points[TentPoleVertex.FrontBottomRight] = [offsetX, -offsetY, -offsetZ];
  points[TentPoleVertex.FrontTopRight] = [offsetX, -offsetY, offsetZ];
  points[TentPoleVertex.FrontBottomLeft] = [-offsetX, -offsetY, -offsetZ];
  points[TentPoleVertex.FrontTopLeft] = [-offsetX, -offsetY, offsetZ];
  points[TentPoleVertex.BackBottomRight] = [offsetX, offsetY, -offsetZ];
  points[TentPoleVertex.BackTopRight] = [offsetX, offsetY, offsetZ];
  points[TentPoleVertex.BackBottomLeft] = [-offsetX, offsetY, -offsetZ];
  points[TentPoleVertex.BackTopLeft] = [-offsetX, offsetY, offsetZ];

  return points.map((point) => addPoints(position, point));
}

export function buildCuboidPointsFromShapeLocation({
  position,
  rotation,
  scale,
}: Pick<CuboidLocationState3D, 'position' | 'rotation' | 'scale'>) {
  const euler = new Euler(...rotation);
  const vectorScale = pointToVector(scale);
  const vectorPosition = pointToVector(position);

  const matrix = new Matrix4();
  matrix.makeRotationFromEuler(euler);
  matrix.scale(vectorScale);
  matrix.setPosition(vectorPosition);

  return buildCuboidPoints3D({
    position: [0, 0, 0],
    scale: [CUBE_SIZE, CUBE_SIZE, CUBE_SIZE],
  }).map((point: Point3D) =>
    roundVector(pointToVector(point).applyMatrix4(matrix)),
  );
}

const reUsableEuler = new Euler();
const reUsableQuaternion = new Quaternion();

export function transformToLocal(
  worldPoint: Point3D,
  cuboidLocation: CuboidLocationState3D,
) {
  reUsableEuler.fromArray(cuboidLocation.rotation);
  reUsableQuaternion.setFromEuler(reUsableEuler);

  return vectorToPoint(
    pointToVector(worldPoint).applyQuaternion(reUsableQuaternion.invert()),
  );
}

export function transformToWorld(
  localPoint: Point3D,
  cuboidLocation: CuboidLocationState3D,
) {
  reUsableEuler.fromArray(cuboidLocation.rotation);
  reUsableQuaternion.setFromEuler(reUsableEuler);

  return vectorToPoint(
    pointToVector(localPoint).applyQuaternion(reUsableQuaternion),
  );
}
